import { z } from 'zod';
/** Device registry — discovery and adoption (U12). */
export const DeviceIdentifier = z.object({
    scheme: z
        .string()
        .regex(/^[a-z][a-z0-9_.-]*$/)
        .max(64),
    value: z.string().trim().min(1).max(128),
});
export const AdoptionStatus = z.enum([
    'waiting',
    'locating',
    'adopted',
    'no_position',
    'outside_areas',
    'no_connection',
    'manual',
]);
const LonLat = z.object({ lon: z.number(), lat: z.number() });
/** GET /api/v1/devices/discovered — devices reported by gateways and not adopted yet (device.provision). */
export const DiscoveredDevice = z.object({
    id: z.uuid(),
    name: z.string(),
    device_type: z.string(),
    protocol: z.string().nullable(),
    identifiers: z.array(DeviceIdentifier),
    discovered_at: z.string().nullable(),
    online: z.boolean(),
    gateway: z.object({ id: z.uuid(), key: z.string(), name: z.string() }).nullable(),
    adoption: z
        .object({
        status: AdoptionStatus,
        attempts: z.number().int(),
        due_at: z.string().nullable(),
        position: LonLat.nullable(),
        position_at: z.string().nullable(),
        updated_at: z.string(),
    })
        .nullable(),
});
export const DiscoveredList = z.object({ items: z.array(DiscoveredDevice) });
export const DeviceTypeList = z.object({
    items: z.array(z.object({ key: z.string(), name_en: z.string() })),
});
const Coordinates = {
    lon: z.number().min(-180).max(180).nullable().optional(),
    lat: z.number().min(-90).max(90).nullable().optional(),
};
/** POST /api/v1/devices/:id/adopt — manual adoption of a discovered device onto a site. */
export const DeviceAdopt = z.object({
    site_id: z.uuid(),
    name: z.string().trim().min(1).max(120).optional(),
    device_type: z.string().max(64).optional(),
    ...Coordinates,
});
/** POST /api/v1/devices — manual registration of a device on a site (before or without a gateway report). */
export const DeviceCreate = z.object({
    site_id: z.uuid(),
    name: z.string().trim().min(1).max(120),
    device_type: z.string().max(64),
    identifiers: z.array(DeviceIdentifier).min(1).max(10),
    ...Coordinates,
});
export const DeviceRecord = z.object({
    id: z.uuid(),
    name: z.string(),
    device_type: z.string(),
    site_id: z.uuid().nullable(),
    lifecycle: z.string(),
});
// ---- scoped registry (device.view on the caller's sites; discovered devices excluded) ----
/** The device another device is reached through (e.g. a concentrator). */
export const DeviceUpstream = z.object({ id: z.uuid(), name: z.string(), model: z.string().nullable() });
export const RegistryDevice = z.object({
    id: z.uuid(),
    name: z.string(),
    device_type: z.string(),
    site_id: z.uuid(),
    /** the project the device was installed under (U20); every device of a client has one */
    project_id: z.uuid().nullable().optional(),
    lon: z.number().nullable(),
    lat: z.number().nullable(),
    capabilities: z.array(z.string()),
    /** protocol profile key the device speaks (catalog data), null when not known yet */
    protocol: z.string().nullable().optional(),
    /** physical model name (catalog data), e.g. LCU-16LTE */
    model: z.string().nullable().optional(),
    /** how the hardware communicates (model catalog data, e.g. lte / radio); display only */
    link: z.string().nullable().optional(),
    /** the device this one is reached through (docs/architecture/13-concentrator-topology-proposal.md); null = own connection */
    upstream: DeviceUpstream.nullable().optional(),
});
/** GET /api/v1/devices?site_id&search&cursor&limit */
export const DeviceList = z.object({
    items: z.array(RegistryDevice),
    next_cursor: z.string().nullable(),
    total: z.number().int().nonnegative(),
});
/**
 * Why the presence is what it is (docs/protocols/connection-liveness-research.md §9, user decision 2026-09-25):
 * connected / superseded_connection (online), socket_closed / idle_timeout / gateway_shutdown (offline),
 * gateway_unreachable (unknown), gateway_reconciled (online or offline after a gateway snapshot); devices reached
 * through an upstream device: upstream_unreachable (unknown), answered_via_upstream (online), not_answering (offline).
 */
export const PresenceReason = z.enum([
    'connected',
    'superseded_connection',
    'socket_closed',
    'idle_timeout',
    'gateway_shutdown',
    'gateway_unreachable',
    'gateway_reconciled',
    'upstream_unreachable',
    'answered_via_upstream',
    'not_answering',
]);
export const DeviceLiveState = z.object({
    sv: z.number().int(),
    presence: z.enum(['online', 'offline', 'unknown']),
    presence_reason: PresenceReason.nullable().optional(),
    presence_changed_at: z.string().nullable().optional(),
    connected_since: z.string().nullable().optional(),
    alarm: z.enum(['info', 'warning', 'critical']).nullable(),
    metrics: z.record(z.string(), z.number()),
    /** observation time of the newest metric (the device's last measurement) */
    measured_at: z.string().nullable().optional(),
    reported: z.record(z.string(), z.unknown()),
    last_seen_at: z.string().nullable(),
});
/** GET /api/v1/devices/snapshot?site_id — static + current state for map and counters. */
export const DeviceSnapshot = z.object({
    as_of_seq: z.number().int(),
    devices: z.array(z.object({ device: RegistryDevice, live: DeviceLiveState })),
});
/** GET /api/v1/devices/:id */
export const DeviceDetail = RegistryDevice.extend({
    lifecycle: z.string(),
    firmware: z.string().nullable(),
    identifiers: z.array(DeviceIdentifier),
    gateway_key: z.string().nullable(),
    connected_since: z.string().nullable(),
    /** where the stored position comes from: manual, device_gps, import (null = no position) */
    location_source: z.string().nullable().optional(),
    /** the caller holds device.control on the device's site (the server still enforces it per command) */
    can_control: z.boolean(),
});
/** GET /api/v1/summary?site_id */
export const Summary = z.object({
    devices: z.number().int(),
    online: z.number().int(),
    offline: z.number().int(),
    unknown: z.number().int(),
    active_alarms: z.number().int(),
    gateways_online: z.number().int(),
    gateways_total: z.number().int(),
    by_type: z.record(z.string(), z.number().int()),
});
/** GET /api/v1/alarms — alarm engine not implemented yet: always scoped, currently empty. */
export const AlarmList = z.object({
    items: z.array(z.object({
        id: z.uuid(),
        device_id: z.uuid(),
        device_name: z.string(),
        site_id: z.uuid(),
        severity: z.enum(['info', 'warning', 'critical']),
        state: z.enum(['open', 'acknowledged', 'resolved']),
        rule_key: z.string(),
        opened_at: z.string(),
    })),
});
/** GET /api/v1/gateways — global gateway.view only. */
export const GatewayList = z.object({
    items: z.array(z.object({
        id: z.uuid(),
        key: z.string(),
        name: z.string(),
        kind: z.string(),
        status: z.enum(['online', 'offline', 'unreachable']),
        software_version: z.string().nullable(),
        connected_devices: z.number().int(),
        last_seen_at: z.string().nullable(),
    })),
});
// ---- commands (docs/architecture/04-gateway-contract.md §7) ----
export const CommandStatus = z.enum([
    'CREATED',
    'PENDING',
    'DISPATCHED',
    'SENT',
    'ACKNOWLEDGED',
    'SUCCESS',
    'FAILED',
    'TIMEOUT',
    'CANCELLED',
]);
export const TERMINAL_COMMAND_STATUSES = [
    'SUCCESS',
    'FAILED',
    'TIMEOUT',
    'CANCELLED',
];
/** POST /api/v1/devices/:id/commands — requires device.control on the device's site. */
export const CommandCreate = z.object({
    capability: z
        .string()
        .regex(/^[a-z][a-z0-9_]*(\.[a-z0-9_]+)+$/)
        .max(96),
    params: z.record(z.string(), z.unknown()).default({}),
    /** client-chosen; the same key from the same user returns the existing command instead of a new one */
    idempotency_key: z.string().min(1).max(128).optional(),
});
export const CommandRecord = z.object({
    id: z.uuid(),
    device_id: z.uuid(),
    capability: z.string(),
    params: z.record(z.string(), z.unknown()),
    status: CommandStatus,
    error_code: z.string().nullable(),
    created_at: z.string(),
    completed_at: z.string().nullable(),
});
export const CommandList = z.object({ items: z.array(CommandRecord) });
// ---- presence history (5-minute samples) ----
/** GET /api/v1/stats/presence?client_id&protocol&hours — summed over the clients visible to the caller. */
const PresencePoint = z.object({
    t: z.string(),
    online: z.number().int(),
    offline: z.number().int(),
    unknown: z.number().int(),
});
export const PresenceHistory = z.object({
    bucket_s: z.number().int(),
    /** with ?by=client: one series per visible client (same buckets as `points`) */
    clients: z
        .array(z.object({ client_id: z.uuid(), name: z.string(), points: z.array(PresencePoint) }))
        .optional(),
    points: z.array(z.object({
        t: z.string(),
        online: z.number().int(),
        offline: z.number().int(),
        unknown: z.number().int(),
    })),
});
// ---- communication protocols (Settings, global settings.manage) ----
export const ProtocolProfileSettings = z.object({
    key: z.string(),
    name: z.string(),
    /** no received byte for this long → the gateway closes the connection, the device becomes OFFLINE */
    offline_after_s: z.number().int().min(30).max(86_400).nullable(),
    /** devices reached through an upstream device (e.g. nodes behind a concentrator) are read this often; null = never */
    downstream_poll_s: z.number().int().min(300).max(86_400).nullable().optional(),
    /** what we learned about the protocol (read-only notes, e.g. observed heartbeat interval) */
    learned: z.record(z.string(), z.unknown()),
    devices: z.number().int(),
});
export const ProtocolSettings = z.object({
    profiles: z.array(ProtocolProfileSettings),
    /** Central lost the gateway for this long → its devices become UNKNOWN */
    gateway_unknown_after_s: z.number().int().min(15).max(86_400),
});
export const ProtocolProfileUpdate = z.object({
    offline_after_s: z.number().int().min(30).max(86_400).nullable(),
    /** omitted = unchanged */
    downstream_poll_s: z.number().int().min(300).max(86_400).nullable().optional(),
});
export const PresenceSettingsUpdate = z.object({
    gateway_unknown_after_s: z.number().int().min(15).max(86_400),
});
// ---- adoption proposals (communes with discovered devices outside every client area) ----
export const AdoptionProposal = z.object({
    /** OpenStreetMap reference of the administrative area, e.g. "relation/123" */
    osm_ref: z.string(),
    name: z.string(),
    county: z.string().nullable(),
    /** SIRUTA code of the commune (Wikidata / OSM), null while unknown */
    siruta: z.string().nullable().optional(),
    kind: z.string().nullable(),
    devices: z.number().int(),
    by_protocol: z.record(z.string(), z.number().int()),
    /** where to put the label (a point on the surface) */
    label: z.tuple([z.number(), z.number()]),
    /** GeoJSON (Multi)Polygon, simplified */
    geometry: z.record(z.string(), z.unknown()),
});
/** GET /api/v1/adoption/proposals — global device.provision. */
export const AdoptionProposals = z.object({
    enabled: z.boolean(),
    /** areas still being looked up (the list grows while > 0) */
    pending: z.number().int(),
    /** discovered devices outside every client area whose commune is not known yet */
    unresolved: z.number().int(),
    areas: z.array(AdoptionProposal),
});
// ---- device queries (read from the device now: position, electrical snapshot) ----
/** POST /api/v1/devices/:id/queries — device.control on the device's site; waits for the device's answer. */
export const DeviceQueryRequest = z.object({
    query_key: z
        .string()
        .regex(/^[a-z][a-z0-9_]*(\.[a-z0-9_]+)+$/)
        .max(96),
    params: z.record(z.string(), z.unknown()).default({}),
});
export const DeviceQueryResponse = z.object({
    query_id: z.uuid(),
    ok: z.boolean(),
    error_code: z.string().nullable(),
    /** decoded answer (e.g. {lon, lat} or {metrics}); `raw` = the device's reply frame (hex) when available */
    result: z.record(z.string(), z.unknown()).nullable(),
    duration_ms: z.number().int(),
    /** location.read: distance from the position we had for the device (legacy import / earlier fix) */
    distance_m: z.number().nullable().optional(),
});
// ---- GPS position correction and release (user decision U19, 2026-10-21; superadmin only) ----
export const GpsCheck = LonLat.extend({
    id: z.uuid(),
    checked_at: z.iso.datetime({ offset: true }),
    /** from the device's current position; null when it has none */
    distance_m: z.number().nullable(),
});
/** Names from the client down to the site, and the client (root site). */
const NamedSite = z.object({ site_id: z.uuid(), client_id: z.uuid(), path: z.array(z.string()) });
/**
 * GET /api/v1/devices/:id/position-correction — the device's GPS readings of the last 60 minutes (newest first) and
 * what accepting them would do. `state`:
 * - none: no reading; within_threshold: the readings agree with the current position (nothing to correct);
 * - need_second: a second reading is needed, not before `next_check_after`; expired: the newest reading is too old;
 * - inconsistent: the two readings are too far apart; ready: `proposed` (their mean) may be accepted or released.
 * `placement` (ready only): same_zone / other_zone (same client, accept moves it to `target`) / other_client /
 * outside_areas (release only).
 */
export const PositionCorrection = z.object({
    threshold_m: z.number(),
    current: LonLat.nullable(),
    site: NamedSite.nullable(),
    checks: z.array(GpsCheck),
    state: z.enum(['none', 'within_threshold', 'need_second', 'expired', 'inconsistent', 'ready']),
    next_check_after: z.iso.datetime({ offset: true }).nullable(),
    /** both readings used (newest first) when a pair exists */
    pair: z.tuple([z.uuid(), z.uuid()]).nullable(),
    /** distance between the two readings */
    spread_m: z.number().nullable(),
    proposed: LonLat.extend({ distance_m: z.number().nullable() }).nullable(),
    placement: z
        .object({
        kind: z.enum(['same_zone', 'other_zone', 'other_client', 'outside_areas']),
        target: NamedSite.nullable(),
    })
        .nullable(),
});
/** POST /api/v1/devices/:id/position-correction/accept — the newest reading of the pair; re-checked server-side. */
export const PositionCorrectionAccept = z.object({ check_id: z.uuid() });
export const PositionCorrectionAccepted = z.object({
    lon: z.number(),
    lat: z.number(),
    site_id: z.uuid(),
    moved: z.boolean(),
});
/**
 * POST /api/v1/devices/:id/release — the device leaves its client and goes back to Discovered; auto-adoption then
 * places it from its own GPS position (possibly at another client). Reason and acknowledgement are mandatory.
 */
export const DeviceRelease = z.object({
    reason: z.string().trim().min(10).max(500),
    acknowledged: z.literal(true),
    /** the GPS reading that showed the problem, when released from the correction dialog */
    check_id: z.uuid().optional(),
});
export const DeviceReleased = z.object({ released: z.literal(true), locating: z.boolean() });
/** GET /api/v1/stats/energy?client_id&days — consumption from cumulative energy counters (kWh). */
export const EnergyStats = z.object({
    days: z.array(z.object({ day: z.string(), kwh: z.number(), devices: z.number().int() })),
    clients: z.array(z.object({ client_id: z.uuid(), name: z.string(), kwh: z.number(), devices: z.number().int() })),
});
/** Traffic totals of one period: TCP payload measured by the gateway and the INFERRED wire estimate (bytes). */
export const TrafficTotals = z.object({
    rx_bytes: z.number().int(),
    tx_bytes: z.number().int(),
    estimated_wire_bytes: z.number().int(),
    connections: z.number().int(),
});
/**
 * GET /api/v1/devices/:id/traffic?days — SIM traffic of the device's own connections (device.view), per local day
 * (Europe/Bucharest). A device reached through an upstream device (e.g. a radio node behind a concentrator) has no SIM of its own:
 * `carried_by` names the device whose SIM carries its traffic and the totals are empty.
 */
export const DeviceTraffic = z.object({
    device_id: z.uuid(),
    carried_by: z.object({ id: z.uuid(), name: z.string().nullable() }).nullable(),
    days: z.array(TrafficTotals.extend({
        day: z.string(),
        rx_segments: z.number().int(),
        tx_segments: z.number().int(),
    })),
    totals: z.object({
        today: TrafficTotals,
        last_7_days: TrafficTotals,
        this_month: TrafficTotals,
        last_month: TrafficTotals,
    }),
});
/**
 * GET /api/v1/devices/:id/reads — the device's read-only queries (capabilities with risk "read") and their parameter
 * schema (JSON Schema subset), so the interface can build the form generically. Running one: POST …/queries.
 */
export const DeviceReads = z.object({
    items: z.array(z.object({
        key: z.string(),
        name_en: z.string(),
        params_schema: z.record(z.string(), z.unknown()),
    })),
});
/** Configuration writes granted to a device that the caller may send (empty without device.configure). */
export const DeviceWrites = DeviceReads;
/** One function of a gateway's catalog as it applies to a device (states of the capability model). */
export const DeviceFunction = z.object({
    function: z.string(),
    capability: z.string().nullable(),
    kind: z.string(),
    safety: z.string(),
    supported_by_vendor: z.string(),
    vendor_ref: z.string().nullable(),
    implemented: z.boolean(),
    tested_offline: z.boolean(),
    tested_live: z.boolean(),
    operationally_enabled: z.boolean(),
    note: z.string().nullable(),
});
/**
 * GET /api/v1/devices/:id/functions — what the gateway serving the device declares for its protocol (device.view):
 * supported by the vendor, implemented, tested, enabled or blocked. Empty when the gateway sent no catalog.
 */
export const DeviceFunctions = z.object({
    gateway_key: z.string().nullable(),
    catalog_version: z.string().nullable(),
    received_at: z.string().nullable(),
    items: z.array(DeviceFunction),
});
//# sourceMappingURL=devices.js.map